import { createPortal } from 'react-dom'
import { useQuery } from '@tanstack/react-query'
import { X, Download, Loader2, Printer } from 'lucide-react'

import { api } from '@/api/client'
import type { ReceivableDetailResponse } from '@/types/models'
import { formatNum } from '@/lib/utils'
import { useFileDownload } from '@/lib/useFileDownload'

interface Props {
  partnerId: number
  partnerName: string
  from: string
  to: string
  onClose: () => void
  /** 같은 업체의 입고·지급·수금 거래내역 창으로 전환(채권 쪽에서도 보이게, 2026-10-16). */
  onShowTx?: () => void
}

/** 미수채권 상세현황 — 거래처 1곳의 라인단위 원장(판매·수금·러닝잔액).
 *  인쇄를 위해 document.body 로 포털(뒤 화면과 분리) + 인쇄 시 나머지 display:none. */
export function ReceivableDetailModal({ partnerId, partnerName, from, to, onClose, onShowTx }: Props) {
  const { downloading, download } = useFileDownload()
  const { data, isLoading } = useQuery({
    queryKey: ['receivable-detail', partnerId, from, to],
    queryFn: () => api<ReceivableDetailResponse>(
      `/api/ledger/receivable/detail?partner=${partnerId}&from=${from}&to=${to}`),
  })

  const exportUrl =
    `/api/ledger/receivable/detail/export?partner=${partnerId}&from=${from}&to=${to}`

  return createPortal(
    <div id="rc-detail-portal">
      {/* 인쇄: 나머지(#root 등)를 흐름에서 제거해 페이지수 정상화 + 모달 static.
          LedgerTab 인쇄 CSS의 body*{visibility:hidden} 을 되돌리려 visible 강제. */}
      <style>{`@media print {
        body > *:not(#rc-detail-portal) { display: none !important; }
        #rc-detail-portal, #rc-detail-portal * { visibility: visible !important; }
        #rc-detail-portal .rc-overlay {
          position: static !important; background: none !important;
          padding: 0 !important; display: block !important;
        }
        #rc-detail-portal .rc-card {
          max-height: none !important; max-width: none !important; width: 100% !important;
          box-shadow: none !important; border-radius: 0 !important;
        }
        #rc-detail-portal .rc-scroll { overflow: visible !important; max-height: none !important; }
        #rc-detail-portal .no-print { display: none !important; }
        #rc-detail-portal thead { display: table-header-group; }
        #rc-detail-portal thead th, #rc-detail-portal tfoot td { position: static !important; }
        #rc-detail-portal table { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
        @page { size: A4 landscape; margin: 10mm; }
      }`}</style>

      <div className="rc-overlay fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
           onClick={onClose}>
        <div className="rc-card bg-white rounded-xl shadow-xl w-full max-w-6xl max-h-[90vh] flex flex-col"
             onClick={(e) => e.stopPropagation()}>
          <header className="flex items-center gap-3 px-5 py-3.5 border-b border-slate-200">
            <div className="flex-1">
              <h3 className="text-lg font-bold text-slate-900">미수채권 상세현황</h3>
              <p className="text-sm text-slate-500">{partnerName} · {from} ~ {to}</p>
            </div>
            {onShowTx && (
              <button onClick={onShowTx}
                      className="no-print flex items-center gap-1.5 border border-purple-300 text-purple-700 hover:bg-purple-50 text-sm font-semibold rounded-lg px-3.5 py-2">
                입고·지급·수금 내역
              </button>
            )}
            <button onClick={() => window.print()} disabled={!data}
                    className="no-print flex items-center gap-1.5 border border-slate-300 text-slate-600 hover:bg-slate-50 text-sm font-semibold rounded-lg px-3.5 py-2 disabled:opacity-40">
              <Printer className="w-4 h-4"/> 인쇄
            </button>
            <button onClick={() => download(exportUrl, `미수채권상세현황_${partnerName}_${from}_${to}.xlsx`)}
                    disabled={downloading || !data}
                    className="no-print flex items-center gap-1.5 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold rounded-lg px-3.5 py-2 disabled:opacity-40">
              {downloading ? <Loader2 className="w-4 h-4 animate-spin"/> : <Download className="w-4 h-4"/>}
              엑셀 다운로드
            </button>
            <button onClick={onClose} className="no-print text-slate-400 hover:text-slate-700 p-1">
              <X className="w-5 h-5"/>
            </button>
          </header>

          <div className="rc-scroll overflow-auto flex-1">
            {isLoading && (
              <div className="flex items-center justify-center py-16 text-slate-500 text-sm">
                <Loader2 className="w-5 h-5 animate-spin mr-2"/> 불러오는 중...
              </div>
            )}
            {data && (
              <table className="w-full text-sm tabular-nums">
                <thead className="sticky top-0 bg-slate-50 text-xs text-slate-500 z-10">
                  <tr className="border-b border-slate-200">
                    <th className="px-2 py-2 text-left font-medium">일자</th>
                    <th className="px-2 py-2 text-left font-medium">품목명/수금유형</th>
                    <th className="px-2 py-2 text-left font-medium">품목코드</th>
                    <th className="px-2 py-2 text-left font-medium">품명</th>
                    <th className="px-2 py-2 text-right font-medium">수량</th>
                    <th className="px-2 py-2 text-right font-medium">단가</th>
                    <th className="px-2 py-2 text-right font-medium">공급가액</th>
                    <th className="px-2 py-2 text-right font-medium">부가세</th>
                    <th className="px-2 py-2 text-right font-medium">합계금액</th>
                    <th className="px-2 py-2 text-right font-medium">수금액</th>
                    <th className="px-2 py-2 text-right font-medium">채권잔액</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-slate-100 bg-amber-50/50">
                    <td className="px-2 py-1.5 font-medium text-slate-600" colSpan={4}>전기(월)이월</td>
                    <td colSpan={5}></td>
                    <td></td>
                    <td className="px-2 py-1.5 text-right font-semibold">{formatNum(data.opening)}</td>
                  </tr>
                  {data.rows.map((r, i) => (
                    <tr key={i} className="border-b border-slate-50 hover:bg-slate-50">
                      <td className="px-2 py-1.5">{r.date}</td>
                      <td className="px-2 py-1.5">{r.kind === 'receipt' ? r.receipt_type : ''}</td>
                      <td className="px-2 py-1.5 font-mono text-xs text-slate-500">{r.item_code || ''}</td>
                      <td className="px-2 py-1.5">{r.item_name || ''}</td>
                      <td className="px-2 py-1.5 text-right">{r.kind === 'sale' ? formatNum(r.qty!) : ''}</td>
                      <td className="px-2 py-1.5 text-right">{r.kind === 'sale' ? formatNum(r.unit_price!) : ''}</td>
                      <td className="px-2 py-1.5 text-right">{r.kind === 'sale' ? formatNum(r.supply!) : ''}</td>
                      <td className="px-2 py-1.5 text-right">{r.kind === 'sale' ? formatNum(r.vat!) : ''}</td>
                      <td className="px-2 py-1.5 text-right">{r.kind === 'sale' ? formatNum(r.total!) : ''}</td>
                      <td className="px-2 py-1.5 text-right text-blue-700">{r.kind === 'receipt' ? formatNum(r.amount!) : ''}</td>
                      <td className="px-2 py-1.5 text-right font-semibold">{formatNum(r.balance)}</td>
                    </tr>
                  ))}
                  {!data.rows.length && (
                    <tr><td colSpan={11} className="text-center text-slate-400 py-10">이 기간에 거래내역이 없습니다.</td></tr>
                  )}
                </tbody>
                <tfoot className="sticky bottom-0 bg-slate-100 font-bold">
                  <tr>
                    <td className="px-2 py-2" colSpan={8}>합계</td>
                    <td className="px-2 py-2 text-right">{formatNum(data.totals.sales)}</td>
                    <td className="px-2 py-2 text-right text-blue-700">{formatNum(data.totals.received)}</td>
                    <td className="px-2 py-2 text-right text-rose-700">{formatNum(data.totals.closing)}</td>
                  </tr>
                </tfoot>
              </table>
            )}
          </div>
        </div>
      </div>
    </div>,
    document.body,
  )
}
